import { useState, useMemo } from "react";
import { useData } from "../contexts/DataContext";
import { List, Columns3, CalendarDays, Plus, ChevronDown, ChevronRight, ClipboardList } from "lucide-react";
import ListView from "../components/views/ListView";
import KanbanView from "../components/views/KanbanView";
import CalendarView from "../components/views/CalendarView";
import TaskDialog from "../components/TaskDialog";
import { FilterBar, useTaskFilters } from "./ProjectView";
import { STATUS_META } from "../components/ui-bits";
import { cn } from "../lib/utils";

const VIEW_TABS = [
  { key: "list", label: "List", icon: List },
  { key: "board", label: "Board", icon: Columns3 },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
];

export default function AllTasks() {
  const { tasks, projects, spaces, users, setOpenTaskId } = useData();
  const [view, setView] = useState("list");
  const [dialog, setDialog] = useState(null);
  const [showSubtasks, setShowSubtasks] = useState(true);
  const [showClosed, setShowClosed] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const filters = useTaskFilters(tasks);

  const visible = useMemo(
    () => (showClosed ? filters.filtered : filters.filtered.filter((t) => t.status !== "done")),
    [filters.filtered, showClosed]
  );

  const projectSections = useMemo(() => {
    return projects.map((p) => {
      const space = spaces.find((s) => s.id === p.space_id);
      const ptasks = visible.filter((t) => t.project_id === p.id && !t.parent_id);
      const groups = Object.entries(STATUS_META)
        .map(([key, m]) => ({ key, label: m.label, color: m.color, projectId: p.id, status: key, tasks: ptasks.filter((t) => t.status === key) }))
        .filter((g) => g.tasks.length > 0);
      return { project: p, space, groups, total: ptasks.length };
    }).filter((s) => s.total > 0);
  }, [projects, spaces, visible]);

  const defaultProjectId = projects[0]?.id;

  const toggleCls = (active) => cn(
    "h-8 px-2.5 text-xs rounded-md border transition-colors",
    active ? "border-[#E31E24] text-[#E31E24] bg-[#E31E24]/10 font-medium" : "border-border text-muted-foreground hover:text-foreground"
  );

  return (
    <div className="p-4 md:p-6 h-full flex flex-col" data-testid="all-tasks-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-[#1B2A6B]/10 text-navy dark:text-blue-300 flex items-center justify-center"><ClipboardList size={16} /></span>
          <h2 className="font-heading text-xl font-bold tracking-tight">All Tasks</h2>
        </div>
        <div className="flex gap-1 border-b border-border">
          {VIEW_TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`view-tab-${key}`} onClick={() => setView(key)}
              className={cn("flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors",
                view === key ? "text-foreground border-[#E31E24] font-medium" : "text-muted-foreground border-transparent hover:text-foreground")}>
              <Icon size={15} />{label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button data-testid="toggle-subtasks" onClick={() => setShowSubtasks((v) => !v)} className={toggleCls(showSubtasks)}>Subtasks</button>
          <button data-testid="toggle-closed" onClick={() => setShowClosed((v) => !v)} className={toggleCls(showClosed)}>Closed</button>
          <FilterBar filters={filters} users={users} />
          <button data-testid="add-task-button" onClick={() => defaultProjectId && setDialog({ group: { projectId: defaultProjectId, status: "todo" } })}
            className="h-8 px-3 flex items-center gap-1.5 text-sm font-medium rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white transition-colors">
            <Plus size={15} />Add Task
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {view === "list" && (
          <div className="space-y-4" data-testid="all-tasks-list">
            {projectSections.map(({ project, space, groups, total }) => {
              const isOpen = !collapsed[project.id];
              return (
                <div key={project.id} data-testid={`project-card-${project.id}`} className="border border-border rounded-lg bg-card overflow-hidden">
                  <div className="flex items-center gap-2 px-3 h-12 border-b border-border">
                    <button data-testid={`project-toggle-${project.id}`} onClick={() => setCollapsed((p) => ({ ...p, [project.id]: !p[project.id] }))}
                      className="text-muted-foreground hover:text-foreground transition-colors">
                      {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </button>
                    <div className="min-w-0">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider leading-tight flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-sm inline-block shrink-0" style={{ backgroundColor: space?.color }} />{space?.name}
                      </p>
                      <p className="text-sm font-heading font-bold leading-tight truncate">{project.name} <span className="text-xs font-normal text-muted-foreground">{total} task{total !== 1 ? "s" : ""}</span></p>
                    </div>
                    <button data-testid={`project-add-task-${project.id}`} onClick={() => setDialog({ group: { projectId: project.id, status: "todo" } })}
                      className="ml-auto h-7 px-2 flex items-center gap-1 text-xs rounded-md text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors shrink-0">
                      <Plus size={13} />Add Task
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-3 pb-3">
                      <ListView groups={groups} users={users} showSubtasks={showSubtasks}
                        onOpenTask={(t) => setOpenTaskId(t.id)}
                        onEditTask={(t) => setDialog({ task: t })}
                        onAddTask={(g) => setDialog({ group: g })}
                        onAddSubtask={(t) => setDialog({ parentId: t.id, group: { projectId: t.project_id } })} />
                    </div>
                  )}
                </div>
              );
            })}
            {projectSections.length === 0 && (
              <div className="text-center py-16 text-muted-foreground" data-testid="all-tasks-empty">
                <p className="text-sm">No tasks found. Create one with the Add Task button.</p>
              </div>
            )}
          </div>
        )}
        {view === "board" && (
          <KanbanView tasks={visible} users={users}
            onOpenTask={(t) => setOpenTaskId(t.id)}
            onAddTask={(g) => setDialog({ group: { status: g.status, projectId: defaultProjectId } })} />
        )}
        {view === "calendar" && (
          <CalendarView tasks={visible}
            onOpenTask={(t) => setOpenTaskId(t.id)}
            onAddTask={(dateKey) => defaultProjectId && setDialog({ group: { projectId: defaultProjectId, status: "todo" }, dueDate: dateKey })} />
        )}
      </div>

      {dialog && (
        <TaskDialog open onClose={() => setDialog(null)}
          projectId={dialog.task?.project_id || dialog.group?.projectId || defaultProjectId}
          projects={projects}
          parentId={dialog.parentId || null}
          task={dialog.task || null}
          defaultStatus={dialog.group?.status || "todo"}
          defaultDueDate={dialog.dueDate || null} />
      )}
    </div>
  );
}
